/** One paged listing, the way every admin data screen reads one.
 *
 * The data screens — uploads, accounts, sessions, logs, the audit trail — are
 * the same shape: a filter, a first page, a "load more" that appends the next
 * one, and a row that changes in place after an action. Writing that five
 * times would be five chances to get the stale-response race wrong, so it is
 * written once. A reply is applied only if it belongs to the most recent
 * request: change a filter twice quickly and the slower first answer must not
 * land on top of the second.
 *
 * `fetcher` is the whole identity of the listing. A screen builds it with
 * `useCallback` over its filters, so a filter change is a new function and a
 * new first page; `reload` asks the same function again.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface Paged<T> {
  items: T[];
  cursor: string | null;
}

export interface PagedListing<T, P extends Paged<T>> {
  /** The first page's envelope: whatever rode along with the rows. */
  page: P | null;
  items: T[];
  /** The first page is in flight and there is nothing to show yet. */
  loading: boolean;
  /** A further page is in flight. */
  loadingMore: boolean;
  error: unknown;
  /** Null once the last page is in. */
  cursor: string | null;
  reload: () => void;
  loadMore: () => void;
  /** Change rows in place — after an action answered with a row's new state. */
  patch: (update: (items: T[]) => T[]) => void;
}

export function usePaged<T, P extends Paged<T>>(
  fetcher: (cursor: string | undefined) => Promise<P>,
): PagedListing<T, P> {
  const [page, setPage] = useState<P | null>(null);
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [generation, setGeneration] = useState(0);
  /** The number of the request whose answer is still wanted. */
  const wanted = useRef(0);

  useEffect(() => {
    const mine = ++wanted.current;
    setLoading(true);
    setError(null);
    void fetcher(undefined).then(
      (reply) => {
        if (mine !== wanted.current) return;
        setPage(reply);
        setItems(reply.items);
        setCursor(reply.cursor);
        setLoading(false);
      },
      (thrown: unknown) => {
        if (mine !== wanted.current) return;
        setError(thrown);
        setLoading(false);
      },
    );
  }, [fetcher, generation]);

  // Unmounting supersedes every request, so nothing lands on a dead screen.
  useEffect(
    () => () => {
      wanted.current += 1;
    },
    [],
  );

  const reload = useCallback(() => setGeneration((n) => n + 1), []);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    const mine = wanted.current;
    setLoadingMore(true);
    void fetcher(cursor).then(
      (reply) => {
        if (mine !== wanted.current) return;
        setItems((have) => [...have, ...reply.items]);
        setCursor(reply.cursor);
        setLoadingMore(false);
      },
      (thrown: unknown) => {
        if (mine !== wanted.current) return;
        setError(thrown);
        setLoadingMore(false);
      },
    );
  }, [cursor, fetcher, loadingMore]);

  const patch = useCallback(
    (update: (items: T[]) => T[]) => setItems((have) => update(have)),
    [],
  );

  return { page, items, loading, loadingMore, error, cursor, reload, loadMore, patch };
}

export default usePaged;
